import { useMemo } from 'react';
import { fmtNum, fmtTRY } from '../../yeni-design/primitives';
import { useSaSorgu } from '../useSatisVerisi';
import { fisOzeti, sayi, type KanalSatiri } from '../ozetHesaplari';
import { kanalSuz, subeSuz } from '../subeSuzgeci';
import { tarihEtiketi } from '../ZamanBlok';
import type { DetayGovdeProps } from './detayTipleri';
import { DagilimTablosu, DeltaHucre, DetayBolum, DetayMesaj, DetayYukleniyor, GunlukCubuklar, OzetKutulari } from './ortak';
import { kisaTutarIsaretsiz } from '../bicim';

export interface KanalToplami {
  grup: string;
  kanal: string;
  ciro: number;
  fis: number;
}

export interface SubeToplami {
  lokasyon: string;
  ciro: number;
  fis: number;
}

/** Grup + alt kanal anahtari. '|' kanal adlarinda gecmez (KANAL_AGACI). */
export const kanalAnahtari = (grup: string, kanal: string) => `${grup}|${kanal}`;

/** 990040 satirlari (alt) kanal basina; ciro azalan. M3 ve M4 de kullanir. */
export function kanalKirilimi(rows: KanalSatiri[]): KanalToplami[] {
  const m = new Map<string, KanalToplami>();
  for (const r of rows) {
    const grup = String(r.kanal_grup);
    const kanal = String(r.kanal);
    const anahtar = kanalAnahtari(grup, kanal);
    const t = m.get(anahtar) ?? { grup, kanal, ciro: 0, fis: 0 };
    t.ciro += sayi(r.net_ciro);
    t.fis += sayi(r.fis_sayisi);
    m.set(anahtar, t);
  }
  return Array.from(m.values()).sort((a, b) => b.ciro - a.ciro);
}

/** 990040 satirlari sube basina; ciro azalan. */
export function subeKirilimi(rows: KanalSatiri[]): SubeToplami[] {
  const m = new Map<string, SubeToplami>();
  for (const r of rows) {
    const lokasyon = String(r.lokasyon);
    const t = m.get(lokasyon) ?? { lokasyon, ciro: 0, fis: 0 };
    t.ciro += sayi(r.net_ciro);
    t.fis += sayi(r.fis_sayisi);
    m.set(lokasyon, t);
  }
  return Array.from(m.values()).sort((a, b) => b.ciro - a.ciro);
}

export const payHesapla = (parca: number, toplam: number): number | null => (toplam !== 0 ? (parca / toplam) * 100 : null);

/**
 * M2 hesabi. Kanal (grup ya da alt kanal) `kanalSuz` ile — ds'lerdeki `kanal_grup = @KANAL OR kanal = @KANAL`
 * kuraliyla ayni. Sayfanin sube suzgeci uygulanir. Onceki donemde kanal satiri yoksa onceki ciro null (0 degil).
 */
export function kanalDetayi(bu: KanalSatiri[], onceki: KanalSatiri[], kanalAdi: string, sube: string | null) {
  const buK = kanalSuz(subeSuz(bu, sube), kanalAdi);
  const oncekiK = kanalSuz(subeSuz(onceki, sube), kanalAdi);
  const oz = fisOzeti(buK, null, null);
  const oncekiSube = new Map(subeKirilimi(oncekiK).map((s) => [s.lokasyon, s.ciro]));
  return {
    satirVar: buK.length > 0,
    ciro: oz.ciro,
    fis: oz.fis,
    oncekiCiro: oncekiK.length > 0 ? fisOzeti(oncekiK, null, null).ciro : null,
    gunluk: oz.gunluk,
    subeler: subeKirilimi(buK).map((s) => ({
      ...s,
      pay: payHesapla(s.ciro, oz.ciro),
      oncekiCiro: oncekiSube.get(s.lokasyon) ?? null,
    })),
  };
}

/** M2 — kanal detayi (spec §7.5). 990040 bu + onceki donem sayfa onbelleginden: 0 ek istek. */
export function KanalDetay({ kaynaklar, donem, oncekiDonem, sube, kanalAdi }: DetayGovdeProps & { kanalAdi: string }) {
  // 040 TEK anahtar (final-fix F-3a): sube/kanal ASLA parametre olmaz, istemcide suzulur.
  const buQ = useSaSorgu<KanalSatiri>(kaynaklar.kanal040, { donem, sube: null, kanal: null }, true, { onbellekten: true });
  const oncekiQ = useSaSorgu<KanalSatiri>(
    kaynaklar.kanal040, { donem: oncekiDonem, sube: null, kanal: null }, true, { onbellekten: true },
  );
  const bu = buQ.data?.data;
  const onceki = oncekiQ.data?.data;
  const d = useMemo(() => kanalDetayi(bu ?? [], onceki ?? [], kanalAdi, sube), [bu, onceki, kanalAdi, sube]);

  let icerik;
  if (!kaynaklar.kanal040) icerik = <DetayMesaj testid="kanal-detay-kaynak-yok">Satış kanalı raporu bu firmada tanımlı değil.</DetayMesaj>;
  else if (buQ.isLoading || oncekiQ.isLoading) icerik = <DetayYukleniyor testid="kanal-detay-yukleniyor" />;
  else if (buQ.isError) icerik = <DetayMesaj testid="kanal-detay-hata">Kanal verisi yüklenemedi.</DetayMesaj>;
  else if (!d.satirVar) icerik = <DetayMesaj testid="kanal-detay-bos">Bu kanalda dönemde satış yok.</DetayMesaj>;
  else
    icerik = (
      <div className="flex flex-col gap-4">
        <OzetKutulari
          kutular={[
            {
              testid: 'kanal-detay-ozet-ciro',
              etiket: 'Ciro',
              deger: fmtTRY(d.ciro),
              ek: <>Önceki döneme göre <DeltaHucre bu={d.ciro} onceki={d.oncekiCiro} /></>,
            },
            { testid: 'kanal-detay-ozet-fis', etiket: 'Fiş', deger: fmtNum(d.fis) },
            { testid: 'kanal-detay-ozet-onceki', etiket: 'Önceki dönem ciro', deger: d.oncekiCiro === null ? '—' : fmtTRY(d.oncekiCiro) },
          ]}
        />
        <DetayBolum baslik="Günlük ciro">
          <GunlukCubuklar
            testid="kanal-detay-gunluk"
            noktalar={d.gunluk.map((g) => ({
              anahtar: g.tarih,
              etiket: tarihEtiketi(g.tarih),
              deger: g.ciro,
              baslik: `${tarihEtiketi(g.tarih)}: ${fmtTRY(g.ciro)}`,
              kisa: kisaTutarIsaretsiz(g.ciro),
            }))}
          />
        </DetayBolum>
        <DetayBolum baslik="Şube dağılımı">
          <DagilimTablosu
            testid="kanal-detay-subeler"
            satirTestid="kanal-detay-sube"
            baslikSutunu="Şube"
            deltaGoster
            satirlar={d.subeler.map((s) => ({ ad: s.lokasyon, ciro: s.ciro, pay: s.pay, sayac: s.fis, oncekiCiro: s.oncekiCiro }))}
          />
        </DetayBolum>
      </div>
    );

  return <div data-testid="kanal-detay">{icerik}</div>;
}
